import { Checkbox, NumberInput, Select, Textarea, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Controller } from 'react-hook-form';
import type { FieldDescriptor, FieldRegistry, LayoutDocument } from './types';
import type { LeaseInputSchema } from '../schemas';
import type { LandLookups } from '../../types/api';
import { CreatableLookupSelect } from './lease-lookup-select';

/**
 * Lease edit-form field registry. Mirror of api/src/lib/lease-form-layout-schema.ts —
 * the field-name list below MUST stay in sync with that file's
 * `LEASE_FORM_FIELD_NAMES` (Task 6).
 *
 * Context bag carries the seeded dropdown options (`useLandLookups()`,
 * `GET /land-data/lookups`) — fetched once by the caller (the lease form
 * drawer / layout editor preview) and threaded down, same pattern as
 * `entry-fields.tsx`'s `docTypes`/`prospects`. Fields never call the query
 * hook themselves: `desc.render` is invoked inline inside a `.map()` in
 * `FormLayoutRenderer`, which would break the Rules of Hooks.
 */

export const LEASE_FORM_FIELD_NAMES = [
  'lease_number',
  'alt_lease_number',
  'lease_type',
  'lease_ownership',
  'fed_rights',
  'status',
  'prospect',
  'field',
  'reviewer_user_id',
  'lessor',
  'lessee',
  'county',
  'state',
  'township',
  'range',
  'section',
  'block',
  'lease_date',
  'expire_date',
  'term',
  'book',
  'page',
  'entry',
  'provided_description',
  'description',
  'gross_acres',
  'net_acres',
  'co_net_acres',
  'orri',
  'royalty',
  'lease_on_minerals',
  'depth_limitations',
  'depth_limitations_remark',
  'area_pugh',
  'depth_pugh',
  'remarks',
  'reviewer_notes',
  'change_remark',
] as const;

export type LeaseFormFieldName = (typeof LEASE_FORM_FIELD_NAMES)[number];

export interface LeaseFormMemberOption {
  value: string;
  label: string;
}

export interface LeaseFormContext {
  lookups: LandLookups;
  /** Current project's members, for the reviewer_user_id dropdown. */
  members: LeaseFormMemberOption[];
  /** owner/superadmin may assign any member; moderator/editor are self-only. */
  canAssignAnyReviewer: boolean;
  currentUserId: string;
}

type LeaseFieldDescriptor = FieldDescriptor<LeaseInputSchema, LeaseFormContext>;

function textProps(
  descriptor: { label: string; placeholder?: string },
  disabled: boolean,
  required = false,
) {
  const props: { label: string; placeholder?: string; disabled?: boolean; required?: boolean } = {
    label: descriptor.label,
  };
  if (descriptor.placeholder !== undefined) props.placeholder = descriptor.placeholder;
  if (disabled) props.disabled = true;
  if (required) props.required = true;
  return props;
}

function textField(
  label: string,
  defaultSpan: number,
  opts: { placeholder?: string; required?: boolean } = {},
): LeaseFieldDescriptor {
  const desc: LeaseFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <TextInput {...textProps(descriptor, disabled, opts.required)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function textareaField(label: string, defaultSpan: number): LeaseFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <Textarea autosize minRows={2} {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
}

function dateField(label: string, defaultSpan: number): LeaseFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DateInput
            label={descriptor.label}
            valueFormat="YYYY-MM-DD"
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            clearable
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function numberField(
  label: string,
  defaultSpan: number,
  opts: { decimalScale?: number } = {},
): LeaseFieldDescriptor {
  const decimalScale = opts.decimalScale ?? 0;
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <NumberInput
            label={descriptor.label}
            decimalScale={decimalScale}
            min={0}
            value={(field.value as number | null) ?? ''}
            onChange={(v) => field.onChange(typeof v === 'number' ? v : null)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function checkboxField(label: string, defaultSpan: number): LeaseFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Checkbox
            label={descriptor.label}
            checked={(field.value as boolean | null) ?? false}
            onChange={(e) => field.onChange(e.currentTarget.checked)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function lookupSelectField(
  label: string,
  kind: keyof LandLookups,
  defaultSpan: number,
): LeaseFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, context, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <CreatableLookupSelect
            label={descriptor.label}
            options={context.lookups[kind]}
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

/**
 * Reviewer dropdown: a Select of the current project's members. For
 * moderator/editor callers (`context.canAssignAnyReviewer === false`) the
 * option list is restricted to self-only — but the CURRENT value must still
 * be visible and round-trip unchanged: the server only re-validates
 * reviewer authorization when the submitted value actually DIFFERS from the
 * stored row (see api/src/routes/leases.ts assertReviewerAssignable), so an
 * editor saving unrelated fields on an owner-reviewed lease must not have
 * this control silently clear/clobber the existing reviewer. If the current
 * value belongs to someone else, the Select is rendered disabled (the user
 * can't reassign it, but it's visible and submits unchanged); if it's
 * already self (or empty), the normal self-only picker applies.
 */
const reviewerField: LeaseFieldDescriptor = {
  label: 'Reviewer',
  defaultSpan: 4,
  render: ({ name, control, disabled, context }) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const currentValue = (field.value as string | null) ?? null;
          const isOtherReviewer =
            !context.canAssignAnyReviewer &&
            currentValue !== null &&
            currentValue !== context.currentUserId;
          const options = context.canAssignAnyReviewer
            ? context.members
            : context.members.filter((m) => m.value === context.currentUserId);
          if (isOtherReviewer && !options.some((m) => m.value === currentValue)) {
            const known = context.members.find((m) => m.value === currentValue);
            options.push({ value: currentValue, label: known?.label ?? currentValue });
          }
          return (
            <Select
              label="Reviewer"
              data={options}
              searchable
              clearable
              value={currentValue}
              onChange={(v) => field.onChange(v ?? null)}
              disabled={disabled || isOtherReviewer}
            />
          );
        }}
      />
    );
  },
};

export const LEASE_FORM_FIELDS: FieldRegistry<LeaseInputSchema, LeaseFormFieldName, LeaseFormContext> = {
  lease_number: textField('Lease number', 6, { required: true }),
  alt_lease_number: textField('Alt lease number', 6),
  lease_type: lookupSelectField('Type', 'lease_type', 4),
  lease_ownership: lookupSelectField('Ownership', 'lease_ownership', 4),
  fed_rights: lookupSelectField('Federal rights', 'fed_rights', 4),
  status: lookupSelectField('Status', 'lease_status', 4),
  prospect: textField('Prospect', 4),
  field: textField('Field', 4),
  reviewer_user_id: reviewerField,

  lessor: textField('Lessor', 6),
  lessee: textField('Lessee', 6),

  county: textField('County', 6),
  state: textField('State', 6),
  township: textField('Township', 3),
  range: textField('Range', 3),
  section: textField('Section', 3),
  block: textField('Block', 3),

  lease_date: dateField('Lease date', 4),
  expire_date: dateField('Expire date', 4),
  term: numberField('Term', 4),

  book: textField('Book', 4),
  page: textField('Page', 4),
  entry: textField('Entry', 4),

  provided_description: textareaField('Provided description', 12),
  description: textareaField('Description', 12),

  gross_acres: numberField('Gross acres', 4, { decimalScale: 2 }),
  net_acres: numberField('Net acres', 4, { decimalScale: 2 }),
  co_net_acres: numberField('CO net acres', 4, { decimalScale: 2 }),

  orri: checkboxField('ORRI', 3),
  royalty: checkboxField('Royalty', 3),
  lease_on_minerals: checkboxField('Lease on minerals', 3),
  depth_limitations: checkboxField('Depth limitations', 3),
  depth_limitations_remark: textareaField('Depth limitations remark', 12),
  area_pugh: checkboxField('Area Pugh', 6),
  depth_pugh: checkboxField('Depth Pugh', 6),

  remarks: textareaField('Remarks', 12),
  reviewer_notes: textareaField('Reviewer notes', 12),
  change_remark: textareaField('Change remark', 12),
};

/**
 * Built-in default. Mirrors api/src/lib/lease-form-layout-schema.ts's
 * `DEFAULT_LAYOUT` groups (identity / parties / PLSS+location / dates /
 * recording / descriptions / acreage / flags / free text).
 */
export const LEASE_DEFAULT_LAYOUT: LayoutDocument<LeaseFormFieldName> = {
  version: 1,
  groups: [
    {
      id: 'identity',
      title: 'Identity',
      fields: [
        { name: 'lease_number', span: 6 },
        { name: 'alt_lease_number', span: 6 },
        { name: 'lease_type', span: 4 },
        { name: 'lease_ownership', span: 4 },
        { name: 'fed_rights', span: 4 },
        { name: 'status', span: 4 },
        { name: 'prospect', span: 4 },
        { name: 'field', span: 4 },
        { name: 'reviewer_user_id', span: 4 },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      fields: [
        { name: 'lessor', span: 6 },
        { name: 'lessee', span: 6 },
      ],
    },
    {
      id: 'plss_location',
      title: 'PLSS / location',
      fields: [
        { name: 'county', span: 6 },
        { name: 'state', span: 6 },
        { name: 'township', span: 3 },
        { name: 'range', span: 3 },
        { name: 'section', span: 3 },
        { name: 'block', span: 3 },
      ],
    },
    {
      id: 'dates',
      title: 'Dates',
      fields: [
        { name: 'lease_date', span: 4 },
        { name: 'expire_date', span: 4 },
        { name: 'term', span: 4 },
      ],
    },
    {
      id: 'recording',
      title: 'Recording',
      fields: [
        { name: 'book', span: 4 },
        { name: 'page', span: 4 },
        { name: 'entry', span: 4 },
      ],
    },
    {
      id: 'descriptions',
      title: 'Descriptions',
      fields: [
        { name: 'provided_description', span: 12 },
        { name: 'description', span: 12 },
      ],
    },
    {
      id: 'acreage',
      title: 'Acreage',
      fields: [
        { name: 'gross_acres', span: 4 },
        { name: 'net_acres', span: 4 },
        { name: 'co_net_acres', span: 4 },
      ],
    },
    {
      id: 'flags',
      title: 'Flags',
      fields: [
        { name: 'orri', span: 3 },
        { name: 'royalty', span: 3 },
        { name: 'lease_on_minerals', span: 3 },
        { name: 'depth_limitations', span: 3 },
        { name: 'depth_limitations_remark', span: 12 },
        { name: 'area_pugh', span: 6 },
        { name: 'depth_pugh', span: 6 },
      ],
    },
    {
      id: 'notes',
      title: 'Notes',
      fields: [
        { name: 'remarks', span: 12 },
        { name: 'reviewer_notes', span: 12 },
        { name: 'change_remark', span: 12 },
      ],
    },
  ],
};
